import React, { useMemo } from 'react'
import {
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableHead,
  Box,
  Typography,
  IconButton,
} from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useIntl } from 'react-intl'
import Checkbox from '../../../../components/editors/checkbox/Checkbox'
import {
  SprintSelectionItem,
  formatSprintLabel,
  EXPANDED_STATUSES,
  COLLAPSED_STATUSES,
  groupByStatus,
} from './types'
import { TableHeaderCell, TableDataCell } from './StyledComponents'
import { SprintStatus } from '../../../../../lib/functions/sprint'
import { colorPalette } from '../../../../style/colorPalette'
import { useExpandableStatuses } from './useExpandableStatuses'

interface Props {
  items: SprintSelectionItem[]
  onCheckChange: (sprintUuid: string, checked: boolean) => void
  formatDate: (dateStr: string) => string
}

type StatusGroupProps = {
  status: SprintStatus
  items: SprintSelectionItem[]
  isExpanded: boolean
  onToggle: (status: SprintStatus) => void
  onCheckChange: (sprintUuid: string, checked: boolean) => void
  formatDate: (dateStr: string) => string
}

const StatusGroup = ({
  status,
  items,
  isExpanded,
  onToggle,
  onCheckChange,
  formatDate,
}: StatusGroupProps) => (
  <React.Fragment>
    <TableRow
      onClick={() => onToggle(status)}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: colorPalette.monotone[1] },
      }}
    >
      <TableDataCell
        colSpan={2}
        sx={{
          fontWeight: 'bold',
          backgroundColor: colorPalette.monotone[1],
          py: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>
            {status}（{items.length}）
          </span>
          <IconButton size="small" tabIndex={-1}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>
      </TableDataCell>
    </TableRow>
    {isExpanded &&
      items.map(item => (
        <TableRow
          key={item.sprintUuid}
          hover={true}
          onClick={() => onCheckChange(item.sprintUuid, !item.checked)}
          sx={{ cursor: 'pointer' }}
        >
          <TableDataCell padding="checkbox">
            <Checkbox
              checked={item.checked}
              onChange={(_, checked) => onCheckChange(item.sprintUuid, checked)}
              color="primary"
            />
          </TableDataCell>
          <TableDataCell sx={{ pl: 4 }}>
            {formatSprintLabel(
              item.sprintName,
              item.startDate,
              item.endDate,
              formatDate
            )}
          </TableDataCell>
        </TableRow>
      ))}
  </React.Fragment>
)

export const SelectionModeView: React.FC<Props> = ({
  items,
  onCheckChange,
  formatDate,
}) => {
  const intl = useIntl()
  const { expandedStatuses, toggleStatus } = useExpandableStatuses()

  const { groups: statusGroups, unsetItems } = useMemo(
    () => groupByStatus(items),
    [items]
  )

  return (
    <>
      {/* 説明テキスト */}
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Typography variant="body2" color="textSecondary">
          {intl.formatMessage({
            id: 'productBacklog.sprintInteractiveAdjustment.selectSprintsDescription',
          })}
        </Typography>
      </Box>
      {/* チェックボックス一覧 */}
      <TableContainer sx={{ maxHeight: 400, px: 3, pb: 2 }}>
        <Table size="small" stickyHeader={true}>
          <TableHead>
            <TableRow>
              <TableHeaderCell padding="checkbox" />
              <TableHeaderCell>
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.sprint',
                })}
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* INPROGRESS → STANDBY（展開状態） */}
            {EXPANDED_STATUSES.filter(status => statusGroups.has(status)).map(
              status => (
                <StatusGroup
                  key={status}
                  status={status}
                  items={statusGroups.get(status)!}
                  isExpanded={expandedStatuses.has(status)}
                  onToggle={toggleStatus}
                  onCheckChange={onCheckChange}
                  formatDate={formatDate}
                />
              )
            )}
            {/* スプリント未設定 */}
            {unsetItems.map(item => (
              <TableRow
                key={item.sprintUuid}
                hover={true}
                onClick={() => onCheckChange(item.sprintUuid, !item.checked)}
                sx={{ cursor: 'pointer' }}
              >
                <TableDataCell padding="checkbox">
                  <Checkbox
                    checked={item.checked}
                    onChange={(_, checked) =>
                      onCheckChange(item.sprintUuid, checked)
                    }
                    color="primary"
                  />
                </TableDataCell>
                <TableDataCell>
                  {formatSprintLabel(
                    item.sprintName,
                    item.startDate,
                    item.endDate,
                    formatDate
                  )}
                </TableDataCell>
              </TableRow>
            ))}
            {/* FINISHED → CANCELED（折りたたみ状態） */}
            {COLLAPSED_STATUSES.filter(status => statusGroups.has(status)).map(
              status => (
                <StatusGroup
                  key={status}
                  status={status}
                  items={statusGroups.get(status)!}
                  isExpanded={expandedStatuses.has(status)}
                  onToggle={toggleStatus}
                  onCheckChange={onCheckChange}
                  formatDate={formatDate}
                />
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
